import React, { useMemo } from 'react';
import { Offer, ProductInterface } from '../../services/exports/Interfaces';
import useMenu from '../../hooks/menu/useMenu';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { CompiledProduct } from '../../providers/MenuProvider';
import { Modals } from '../../modules/mobile/exports/Enums';
import classnames from 'classnames';
import { swipeableCssClass } from '../../modules/mobile/exports/Constants';
import CustomCarousel, { Item } from '../../components-library/carousel/CustomCarousel';
import OfferProduct from './OfferProduct';
import useScreenType from '../../hooks/utility/useScreenType';
import useCategoryRestriction from '../../hooks/menu/useCategoryRestriction';

type Props = {
  offer: Offer;
  products: number[];
  title: string;
  description: string;
  hasAddBtn?: boolean;
};

const OfferProducts = (props: Props) => {
  const { compileProduct } = useMenu();
  const { toggleMenuItemModal, toggleOfferModal } = useAppMethods();
  const closeOfferModal = () => toggleOfferModal({ openModal: false });
  const { isMobileApp } = useMobileApp();
  const { isDesktop } = useScreenType();
  const { openModal, closeModal } = useModalRouter();
  const getRestriction = useCategoryRestriction();
  const products: CompiledProduct[] = useMemo(
    () => props.products?.map(compileProduct)?.filter(Boolean),
    [props.products, compileProduct],
  );

  const restrictedTooltipByProductId = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();

    if (!products?.length) {
      return map;
    }

    for (const product of products) {
      const { isRestricted, tooltip } = getRestriction(product.category_ids);

      if (isRestricted) {
        map.set(product.id, tooltip);
      }
    }

    return map;
  }, [products, getRestriction]);
  const onCloseMenuItemModal = () => toggleOfferModal({ openModal: true, offer: props.offer });
  const toggleMobileAppProduct = (product: ProductInterface) => {
    return openModal({
      id: Modals.MenuItem,
      presentation: {
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle: false,
        cssClass: classnames(swipeableCssClass),
      },
      props: {
        product,
        onCloseModal: closeModal,
        offer: props.offer,
        onBack: () => {
          openModal({
            id: Modals.OfferModal,
            props: {
              offer: props.offer,
            },
          });
        },
        showProductAddedToast: true,
      },
    });
  };

  const toggleProduct = (product: ProductInterface) => {
    closeOfferModal();

    if (!isMobileApp) {
      return toggleMenuItemModal({
        openModal: true,
        product: product,
        onBack: onCloseMenuItemModal,
      });
    }

    toggleMobileAppProduct(product);
  };

  const items: Item[] = useMemo(
    () =>
      products?.map((product) => {
        const restrictedTooltip = restrictedTooltipByProductId.get(product.id);
        const isRestricted = Boolean(restrictedTooltip);

        return {
          data: product,
          render: () => (
            <OfferProduct
              product={product}
              key={product.id}
              isRestricted={isRestricted}
              restrictedTooltip={restrictedTooltip}
              {...(props.hasAddBtn && !isRestricted && { onClick: () => toggleProduct(product) })}
              hasAddBtn={props.hasAddBtn}
            />
          ),
        };
      }),
    [products, props.hasAddBtn, restrictedTooltipByProductId],
  );
  const itemHeight = props.hasAddBtn ? 285 : 185;
  const itemsPerSlide = isDesktop ? 4 : undefined;

  return (
    <>
      <CustomCarousel
        itemsPerSlide={itemsPerSlide}
        items={items}
        arrowsPosition="top"
        arrowsClassName="lg:mr-lg mr-md"
        itemWidth={185}
        itemHeight={itemHeight}
        title={<h3 className="text-md">{props.title}</h3>}
        {...(props.description && { description: <p className="text-muted-foreground">{props.description}</p> })}
      />
    </>
  );
};

export default OfferProducts;
